import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { common } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import {
  useGetJobApplications,
  useUpdateRejectShift,
} from 'src/pages/ProjectDetailsPage/api';
import DataTable from 'src/components/DataTable';
import { hoursToString } from 'src/utils/datetime';
import StudentLink from 'src/components/StudentLink';
import { REJECT_REASON } from 'src/pages/ProjectDetailsPage/api/queries/useGetRejectReason';

import messages from '../../messages';
import { useStyles } from '../../JobDetails.styled';
import RejectReasonModal from '../RejectReasonModal/RejectReasonModal';
import { JobApplicationTabProps } from '../JobApplicationsAll/JobApplicationsAll';

import type { DefaultTheme } from '@mui/system';

export default function JobApplicationsAccepted({
  job,
  jobUuid,
  filters,
  candidate,
  onCandidateSelect,
}: JobApplicationTabProps) {
  const { formatMessage } = useIntl();

  const classes = useStyles();

  const [selected, setSelected] = useState<UCM.JobApplicationType | null>(null);
  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: jobApplications, isLoading } = useGetJobApplications({
    jobUuid,
    type: 'accepted',
    filters,
    params,
    enabled: Boolean(jobUuid),
  });

  const { mutate: rejectShift, isPending: isPendingReject } =
    useUpdateRejectShift();

  const handleReject = useCallback(
    (reason: string) => {
      if (selected?.uuid) {
        rejectShift(
          {
            jobUuid,
            applicationUuid: selected?.uuid,
            reason,
            type: 'cancel',
          },
          {
            onSettled: () => {
              setSelected(null);
            },
          },
        );
      }
    },
    [selected?.uuid, jobUuid],
  );

  const pressCandidate = useCallback(
    (item: UCM.JobApplicationType | null) => {
      let candidate = (item as UCM.JobApplicationType | string) ?? null;

      if (!job?.isFlex) {
        return setSelected(item);
      }

      onCandidateSelect?.(candidate);
    },
    [job?.isFlex, onCandidateSelect],
  );

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string;
  }> = useMemo(
    () => [
      {
        id: 'applicant',
        label: formatMessage(messages.applicantLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant) return '-';
          const { id, fullName, uuid } = item.applicant;
          return <StudentLink label={`${id} - ${fullName}`} uuid={uuid} />;
        },
      },
      {
        id: 'appliedCount',
        align: 'center',
        label: formatMessage(messages.appliedToLabel),
        render: (appliedCount: number) => (
          <Typography variant="inherit">{appliedCount ?? '-'}</Typography>
        ),
      },
      {
        id: 'yearlyWorkdaysCount',
        label: formatMessage(messages.workdayLabel),
        render: (yearlyWorkdaysCount: number) => (
          <Typography variant="inherit">{yearlyWorkdaysCount}</Typography>
        ),
        align: 'center',
      },
      {
        id: 'cancelRate',
        label: formatMessage(messages.cancelRateLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.applicationsCancelledRatio) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.applicationsCancelledRatio}%
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'timeToConfirm',
        label: formatMessage(messages.timeToConfirmLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.confirmationTime) return '-';
          return (
            <Typography variant="inherit">
              {hoursToString(item.applicant.confirmationTime)}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'logIn',
        align: 'center',
        label: formatMessage(messages.logInLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.lastLoginAt)
            return (
              <CloseOutlinedIcon
                sx={{ color: '#904A44', width: 24, height: 24 }}
              />
            );
          return (
            <Typography variant="inherit">
              {item.applicant.lastLoginAt}
            </Typography>
          );
        },
      },
      {
        id: 'initialScore',
        label: formatMessage(messages.initialScoreColumnLabel),
        size: 'small',
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.initialRatingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.initialRatingScore}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'ratingScore',
        align: 'center',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.ratingScore}
            </Typography>
          );
        },
      },
      {
        id: 'prescreeningRating',
        label: formatMessage(messages.preScreenLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          const { prescreeningRating } = item.applicant;

          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={1}>
                  {Object.entries(prescreeningRating).map(([key, value]) => (
                    <Typography
                      key={key}
                      variant="inherit"
                      textTransform="capitalize"
                    >
                      {key}: {value}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Typography variant="inherit">
                {prescreeningRating.average}
              </Typography>
            </Tooltip>
          );
        },
        align: 'center',
      },
      {
        id: 'profile',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant?.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant?.status?.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'lastWorkInDays',
        label: formatMessage(messages.lastJobLabel),
        render: (lastWorkInDays: number | null) => (
          <Typography variant="inherit">
            {lastWorkInDays ? `${lastWorkInDays}d` : '-'}
          </Typography>
        ),
        align: 'center',
      },
      {
        id: 'daysSinceAccepted',
        align: 'center',
        label: formatMessage(messages.daysSinceAcceptedLabel),
        render: (daysSinceAccepted: string) => (
          <Typography variant="inherit">{daysSinceAccepted ?? '-'}</Typography>
        ),
      },
    ],
    [],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const tableFooterSx = useCallback(
    (theme: DefaultTheme) => ({
      borderTop: `thin solid ${theme.palette.grey[200]}`,
    }),
    [],
  );

  return (
    <Grid size={{ xs: 12 }}>
      <DataTable
        columns={columns}
        rows={jobApplications?.data}
        className={classes.tableContainer}
        loading={isLoading}
        activeRow={job?.isFlex ? candidate : selected}
        pressRow={pressCandidate}
        checkActiveKey="applicant.id"
        onPaginate={handlePagination}
        pagination={jobApplications?.meta}
      />
      {!job?.isFlex && (
        <Stack
          p={2}
          gap={1}
          sx={tableFooterSx}
          flexDirection="row"
          bgcolor={common.white}
          justifyContent="flex-end"
        >
          <RejectReasonModal
            type={REJECT_REASON.CANCEL_APPLICATION}
            modalLabel={formatMessage(messages.rejectModalLabel)}
            buttonLabel={formatMessage(messages.rejectButton)}
            onConfirm={handleReject}
            enabledFetch={Boolean(jobUuid)}
            disabledButton={!selected || isPendingReject}
            isLoading={isPendingReject}
          />
        </Stack>
      )}
    </Grid>
  );
}
